import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import ServerStatus from "../components/ServerStatus";
import AuthBrandPanel from "../components/AuthBrandPanel";
import GoogleSignInButton from "../components/GoogleSignInButton";
import api from "../lib/api";
import { Check, X, Zap, ArrowRight, ChevronLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { MONTHLY_PRICE, YEARLY_PRICE, PLAN_FEATURES, planFeatureText } from "../lib/plans";

// Shared input styling — rounded, clear blue focus ring
const INPUT_CLS =
  "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-muted-foreground/50 focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none transition-all";

export default function Signup() {
  const [params]    = useSearchParams();
  const [role, setRole]             = useState("candidate");
  const [name, setName]             = useState("");
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [showPw, setShowPw]         = useState(false);
  const [targetJobTitle, setTargetJobTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [step, setStep]               = useState("plan");
  const [selectedPlan, setSelectedPlan] = useState("basic");
  const [billing, setBilling]         = useState("monthly");

  const [promoCode, setPromoCode]           = useState("");
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult]       = useState(null); // { discount_percent, description, stripe_promo_code_id }
  const [promoError, setPromoError]         = useState("");

  const { signup, loginWithGoogle, checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (params.get("stripe_success") === "1") {
      checkAuth().then(() => { toast.success("Welcome to Premium!"); navigate("/dashboard"); });
      return;
    }
    if (params.get("stripe_cancelled") === "1") {
      toast.info("Payment cancelled — you can upgrade anytime from your dashboard.");
      setStep("plan");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoleChange = (r) => {
    setRole(r);
    setStep(r !== "candidate" ? "form" : "plan");
  };

  const validatePromo = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoValidating(true);
    setPromoError("");
    setPromoResult(null);
    try {
      const { data } = await api.post("/public/validate-promo", { code, billing });
      setPromoResult(data);
    } catch (e) {
      setPromoError(e?.response?.data?.detail || "Invalid promo code");
    } finally { setPromoValidating(false); }
  };

  const clearPromo = () => { setPromoCode(""); setPromoResult(null); setPromoError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signup(email, password, name, role, targetJobTitle.trim() || null);
      if (role === "candidate" && selectedPlan === "premium") {
        const { data } = await api.post("/stripe/create-checkout-session", {
          billing,
          stripe_promo_code_id: promoResult?.stripe_promo_code_id || null,
        });
        window.location.href = data.checkout_url;
        return;
      }
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleCredential = async (credential, r) => {
    try {
      const user = await loginWithGoogle(credential, r);
      toast.success("Welcome");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Google sign-in failed");
    }
  };

  const yearlyMonthly = (YEARLY_PRICE / 12).toFixed(2);
  const isPremium     = role === "candidate" && selectedPlan === "premium";

  return (
    <main className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

      {/* ══════════════ LEFT — FORM ══════════════ */}
      <div className="flex items-center justify-center p-6 lg:p-14 bg-white">
        <div className="w-full max-w-[420px]">
          {/* Mobile wordmark */}
          <Link to="/" className="lg:hidden mb-8 inline-block">
            <Logo className="h-9 w-auto" />
          </Link>

          <ServerStatus />

          {/* Step breadcrumb */}
          <div className="flex items-center gap-2 mb-8">
            <div className={`h-1 w-8 rounded-full transition-all duration-300 ${step === "plan" ? "bg-[#0025D8]" : "bg-[#7C3AED]"}`} />
            <div className={`h-1 w-8 rounded-full transition-all duration-300 ${step === "form" ? (isPremium ? "bg-[#7C3AED]" : "bg-[#0025D8]") : "bg-border"}`} />
            <span className="font-mono text-[10px] text-muted-foreground ml-1 tracking-widest">
              {step === "plan" ? "STEP 1 OF 2" : "STEP 2 OF 2"}
            </span>
          </div>

          {/* ── PLAN STEP ── */}
          {step === "plan" && (
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#0025D8] mb-2">CREATE ACCOUNT</div>
              <h1 className="font-display font-black text-[2.6rem] leading-[1.1] tracking-tight text-[#0F172A] mb-1">
                Choose how you<br />want to grow.
              </h1>
              <p className="text-sm text-muted-foreground mb-7">Start free, upgrade when you're ready.</p>

              {/* Role toggle */}
              <div className="flex items-center gap-1 p-1 bg-secondary rounded-xl border border-border mb-7">
                {[["candidate", "I'm a candidate"], ["company", "I'm hiring"]].map(([r, label]) => (
                  <button key={r} type="button" onClick={() => handleRoleChange(r)}
                    className={`flex-1 py-2.5 text-sm font-semibold tracking-wide rounded-lg transition-all duration-200 ${
                      role === r ? "bg-white text-[#0F172A] shadow-sm" : "text-muted-foreground hover:text-foreground"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>

              {role === "candidate" ? (
                <PlanSelector
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  billing={billing}
                  setBilling={setBilling}
                  yearlyMonthly={yearlyMonthly}
                  onContinue={() => setStep("form")}
                />
              ) : (
                <div className="space-y-5">
                  <div className="rounded-2xl border border-border p-5 bg-secondary/40">
                    <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-1">COMPANY PLAN</div>
                    <div className="font-display font-black text-3xl text-[#0F172A] mb-1">Free to start</div>
                    <p className="text-sm text-muted-foreground">Screen unlimited candidates with AI. No credit card required.</p>
                  </div>
                  <button type="button" onClick={() => setStep("form")}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-bold bg-[#0025D8] hover:bg-[#001CB3] text-white shadow-lg shadow-[#0025D8]/20 transition-all">
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              <p className="mt-7 text-sm text-muted-foreground">
                Have an account?{" "}
                <Link to="/login" className="text-[#0025D8] font-semibold hover:underline">Log in</Link>
              </p>
            </div>
          )}

          {/* ── FORM STEP ── */}
          {step === "form" && (
            <div>
              <button type="button" onClick={() => setStep("plan")}
                className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-6 group">
                <ChevronLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" />
                BACK
              </button>

              <div className="font-mono text-[10px] tracking-[0.25em] text-[#0025D8] mb-2">CREATE ACCOUNT</div>
              <h1 className="font-display font-black text-[2.6rem] leading-[1.1] tracking-tight text-[#0F172A] mb-6">
                Almost there.
              </h1>

              {/* Plan confirmation chip */}
              {role === "candidate" && (
                <div className={`inline-flex items-center gap-2 px-3 py-2 mb-6 rounded-lg border ${
                  isPremium
                    ? "border-indigo-400/40 bg-indigo-500/8 text-indigo-600"
                    : "border-border bg-secondary/60 text-muted-foreground"
                }`}>
                  {isPremium ? <Zap className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                  <span className="font-mono text-[10px] font-bold tracking-wider">
                    {isPremium
                      ? `PREMIUM — $${billing === "monthly" ? `${MONTHLY_PRICE}/mo` : `${YEARLY_PRICE}/yr`}`
                      : "BASIC — FREE"}
                  </span>
                  <button type="button" onClick={() => setStep("plan")}
                    className="ml-1 opacity-50 hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Google sign-in (Google Identity Services) — hidden until a Client ID is configured */}
              {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                <>
                  <div data-testid="google-signup" className="mb-5">
                    <GoogleSignInButton role={role} text="signup_with" onCredential={handleGoogleCredential} />
                  </div>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-border" />
                    <span className="font-mono text-[10px] text-muted-foreground tracking-widest">OR</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                </>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                    {role === "company" ? "Company Name" : "Full Name"}
                  </label>
                  <input required value={name} onChange={(e) => setName(e.target.value)}
                    placeholder={role === "company" ? "Acme Corp" : "Jane Doe"}
                    className={INPUT_CLS} data-testid="signup-name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Email Address</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className={INPUT_CLS} data-testid="signup-email" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Password</label>
                  <div className="relative">
                    <input type={showPw ? "text" : "password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className={`${INPUT_CLS} pr-11`} data-testid="signup-password" />
                    <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#0F172A] transition-colors"
                      aria-label={showPw ? "Hide password" : "Show password"}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {role === "candidate" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                      Target Job Title
                      <span className="ml-2 font-normal text-muted-foreground/60">optional</span>
                    </label>
                    <input value={targetJobTitle} onChange={(e) => setTargetJobTitle(e.target.value)}
                      placeholder="e.g. QA Engineer, Product Manager"
                      className={INPUT_CLS} data-testid="signup-target-job-title" />
                  </div>
                )}

                {role === "candidate" && selectedPlan === "premium" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                      Promo code
                      <span className="ml-2 font-normal text-muted-foreground/60">optional</span>
                    </label>
                    {promoResult ? (
                      <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#10B981] bg-[#10B981]/5">
                        <Check className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                        <span className="text-sm font-semibold text-[#047857] flex-1">{promoResult.description}</span>
                        <button type="button" onClick={clearPromo} className="text-muted-foreground hover:text-[#0F172A] transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          value={promoCode}
                          onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), validatePromo())}
                          placeholder="e.g. LAUNCH20"
                          className={`${INPUT_CLS} flex-1 font-mono uppercase`}
                          data-testid="signup-promo-code"
                        />
                        <button
                          type="button" onClick={validatePromo}
                          disabled={!promoCode.trim() || promoValidating}
                          className="px-4 py-2.5 rounded-xl border border-border text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {promoValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                        </button>
                      </div>
                    )}
                    {promoError && <p className="text-xs text-[#E11D48] mt-1.5">{promoError}</p>}
                  </div>
                )}

                <button type="submit" disabled={submitting} data-testid="signup-submit"
                  className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-bold shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed mt-2 ${
                    isPremium
                      ? "bg-[#0F172A] hover:bg-[#1E293B] text-white shadow-[#0F172A]/20"
                      : "bg-[#0025D8] hover:bg-[#001CB3] text-white shadow-[#0025D8]/20"
                  }`}>
                  {submitting
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> {isPremium ? "Redirecting to payment…" : "Creating account…"}</>
                    : <>{isPremium ? "Create account & pay" : "Create account"} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>

              <p className="mt-6 text-sm text-muted-foreground">
                Have an account?{" "}
                <Link to="/login" className="text-[#0025D8] font-semibold hover:underline">Log in</Link>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════ RIGHT — BRAND PANEL ══════════════ */}
      <AuthBrandPanel />
    </main>
  );
}

/* ══════════════════════════════════════════════════════ */
/*  Plan Selector                                         */
/* ══════════════════════════════════════════════════════ */
function PlanSelector({ selectedPlan, setSelectedPlan, billing, setBilling, yearlyMonthly, onContinue }) {
  const isPremium = selectedPlan === "premium";

  return (
    <div className="space-y-4">

      {/* Billing toggle */}
      <div className="flex items-center gap-1 p-1 bg-secondary rounded-xl border border-border w-fit">
        {[["monthly", "Monthly"], ["yearly", "Yearly"]].map(([b, label]) => (
          <button key={b} type="button" onClick={() => setBilling(b)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center gap-2 ${
              billing === b ? "bg-white text-[#0F172A] shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}>
            {label}
            {b === "yearly" && (
              <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wider transition-colors ${
                billing === "yearly" ? "bg-[#10B981] text-white" : "bg-[#10B981]/15 text-[#10B981]"
              }`}>−17%</span>
            )}
          </button>
        ))}
      </div>

      {/* PREMIUM card — deep navy (shown first for anchoring) */}
      <button type="button" onClick={() => setSelectedPlan("premium")}
        className={`w-full text-left rounded-2xl border transition-all duration-200 relative overflow-hidden ${
          selectedPlan === "premium"
            ? "border-indigo-500 ring-2 ring-indigo-500/25"
            : "border-indigo-900/60 hover:border-indigo-600/70"
        } bg-[#0F172A]`}>

        {/* Indigo glow line at top */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-70" />
        {/* Subtle orb inside card */}
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)" }} />

        <div className="p-5 relative">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono text-[10px] tracking-[0.2em] text-indigo-400">PREMIUM</span>
                <span className="font-mono text-[8px] px-1.5 py-0.5 rounded bg-indigo-600 text-white tracking-wider">POPULAR</span>
              </div>
              <div className="font-display font-black text-4xl tracking-tight text-white">
                ${billing === "monthly" ? MONTHLY_PRICE : yearlyMonthly}
              </div>
              <div className="font-mono text-[11px] text-white/40 mt-1">
                {billing === "monthly" ? "per month" : `per month · $${YEARLY_PRICE} billed yearly`}
              </div>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
              selectedPlan === "premium" ? "border-indigo-500 bg-indigo-500" : "border-white/20"
            }`}>
              {selectedPlan === "premium" && <Check className="w-3 h-3 text-white" />}
            </div>
          </div>
          <div className="h-px bg-white/[0.08] mb-4" />
          <div className="space-y-2.5">
            {PLAN_FEATURES.map((f) => (
              <div key={f.label} className="flex items-center gap-2.5">
                <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                <span className="text-[12px] text-white/75">
                  {planFeatureText(f, "premium")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </button>

      {/* BASIC card (shown second — free feels like a relief after seeing the price) */}
      <button type="button" onClick={() => setSelectedPlan("basic")}
        className={`w-full text-left rounded-2xl border transition-all duration-200 bg-white ${
          selectedPlan === "basic"
            ? "border-[#0025D8] ring-2 ring-[#0025D8]/20"
            : "border-border hover:border-foreground/30"
        }`}>
        <div className="p-5">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-1.5">BASIC</div>
              <div className="font-display font-black text-4xl tracking-tight text-[#0F172A]">Free</div>
              <div className="font-mono text-[11px] text-muted-foreground mt-1">forever · no credit card</div>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mb-1 transition-all ${
              selectedPlan === "basic" ? "border-[#0025D8] bg-[#0025D8]" : "border-border"
            }`}>
              {selectedPlan === "basic" && <Check className="w-3 h-3 text-white" />}
            </div>
          </div>
          <div className="h-px bg-border mb-4" />
          <div className="space-y-2.5">
            {PLAN_FEATURES.map((f) => (
              <div key={f.label} className="flex items-center gap-2.5">
                {f.basic === false
                  ? <X className="w-3.5 h-3.5 text-border flex-shrink-0" />
                  : <Check className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />}
                <span className={`text-[12px] ${f.basic === false ? "text-muted-foreground/40 line-through decoration-border" : "text-foreground"}`}>
                  {planFeatureText(f, "basic")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </button>

      {/* CTA */}
      <button type="button" onClick={onContinue}
        className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-bold shadow-lg transition-all duration-200 ${
          isPremium
            ? "bg-[#0F172A] hover:bg-[#1E293B] text-white border border-indigo-500/50 shadow-[#0F172A]/20"
            : "bg-[#0025D8] hover:bg-[#001CB3] text-white shadow-[#0025D8]/20"
        }`}>
        {isPremium ? "Continue with Premium" : "Get Started Free"}
        <ArrowRight className="w-4 h-4" />
      </button>

      {isPremium && (
        <p className="text-center font-mono text-[10px] text-muted-foreground">
          You'll be redirected to Stripe after account creation.
        </p>
      )}
    </div>
  );
}
